import React, { Dispatch, memo, SetStateAction, useContext, useEffect, useRef, useState } from 'react';
import Icon, { Icons } from '../../Icon';
import routes from '../../../constants/routes';
import useTranslations from '@hooks/useTranslations';
import Link from '../../CustomNextLink';
import { Link as RelativeLink } from '../../../../i18n';
import UserDuck from '../../../redux/user/user.duck';
import { useDispatch } from 'react-redux';
import { BRANDID_MY_ACCOUNT_URL, BRANDID_LOGOUT_URL } from '../../../constants/myAccount';
import sessionInfoApi from '../../../services/core/seassionInfoApi.service';
import { getSessionIdCookie } from '@utils/Session.utils';
import { useCarStockJourneyCheck } from '@hooks/useCarStockJourneyCheck';
import { useRouter } from 'next/router';
import { useGTM } from '@hooks/useGTM';
import {
    B2B_URL,
    B2C_URL,
    IS_B2B,
    IS_B2C,
    IS_CLIENT,
    isBrandAC,
    isBrandAP,
    isBrandDS,
    MARKET,
    MARKETS,
} from '../../../constants/main';
import { IsHomepageContext } from '../../../contexts/IsHomepageContext';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST } from '../../../context/featureSwitch';
import { usePxPendingUploads } from '@hooks/usePxPendingUploads';
import { useTheme } from '@hooks/useTheme';
import { useUserDuck } from '@hooks/useUserDuck';
import NeedHelpSection from '../NeedHelpSection';
import useOnClickOutside from '@hooks/useOnClickOutside';
import isMobile from 'is-mobile';
import { useCreateDeal } from '@hooks/useCreateDeal';
import { GtmData } from '../../../types/gtm';
import { buildLanguagePathname } from '@utils/url.utils';
import { EUserLoginType } from '../../../interfaces/User';

const HeaderNavigation: React.FC<{ showMenu: boolean; setshowMenu: Dispatch<SetStateAction<boolean>> }> = memo(
    ({ showMenu, setshowMenu }) => {
        const theme = useTheme();
        const { t } = useTranslations();
        const router = useRouter();
        const [isHelpSectionOpen, setIsHelpSectionOpen] = useState(false);
        const isHomepage = useContext(IsHomepageContext);
        const needHelpRef = useRef();
        const needHelpButtonRef = useRef();

        const dispatch = useDispatch();
        const { isUserLoggedIn, FetchMyOrders, userAuth } = useUserDuck();
        const { pushToDataLayer } = useGTM();

        useEffect(() => {
            if (isUserLoggedIn) dispatch(FetchMyOrders(1));
        }, [isUserLoggedIn, FetchMyOrders, dispatch]);

        useEffect(() => {
            if (IS_CLIENT && isMobile()) {
                document.body.style.overflow = isHelpSectionOpen ? 'hidden' : null;
            }
        }, [isHelpSectionOpen]);

        const { pxPendingUploads } = usePxPendingUploads();

        const handleNeedHelpClick = () => {
            setIsHelpSectionOpen((isOpen) => {
                setshowMenu(false);
                return !isOpen;
            });
        };

        const handleClickOutside = () => {
            setIsHelpSectionOpen(false);
        };

        useOnClickOutside(needHelpRef, handleClickOutside, [needHelpButtonRef]);

        const logoutUser = async (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            await sessionInfoApi.logout(getSessionIdCookie());
            dispatch(UserDuck.actionSetUserLogged(false));

            if (IS_CLIENT) {
                let redirectUrl = window.location.host;

                if (router.pathname.includes(routes.LOGIN)) {
                    redirectUrl = `${redirectUrl}${routes.LOGIN}`;
                } else if (
                    router.pathname.includes(routes.BASKET) ||
                    router.pathname.includes(routes.CHECKOUT_MY_DETAILS) ||
                    router.pathname.includes(routes.CHECKOUT_PRE_ORDER)
                ) {
                    redirectUrl = `${redirectUrl}${routes.ROOT}`;
                } else {
                    redirectUrl = window.location.href;
                }

                const logOutURL = BRANDID_LOGOUT_URL.replace('{redirectUrlLogout}', redirectUrl);
                window.location.href = logOutURL;
            }
        };

        const { isStockJourney } = useCarStockJourneyCheck();

        const isBrandRecommendsJourneyAllowed = useFeatureSwitchEnabled(
            FEATURES_LIST.FEATURE_SWITCH_BRAND_RECOMMENDS_ENABLED
        );

        const onMyAccountClick = () => {
            pushToDataLayer({
                event: 'uaevent',
                eventCategory: 'Header',
                eventAction: 'Redirection::Internal',
                eventLabel: 'My Account',
            } as GtmData);
        };

        const renderLogoutBox = (extended = false) => {
            if (extended) {
                return (
                    <>
                        <a
                            href="#"
                            id="TESTING_MY_ACC"
                            data-testid="TESTING_MY_ACC"
                            onClick={() => {
                                setshowMenu(!showMenu);
                                onMyAccountClick();
                            }}
                        >
                            <Icon
                                height={24}
                                name={Icons.Account}
                                className={`
                        account
                        ${isUserLoggedIn ? 'isLogged' : ''}
                        ${isStockJourney ? 'isStockJourney' : ''}
                      `}
                            />
                            <p className="header__nav-description">{t('header.nav.myAccount')}</p>
                        </a>
                        {pxPendingUploads.length > 0 && (
                            <Icon
                                height={24}
                                name={Icons.InfoCircle}
                                className={`pxAttention count-${pxPendingUploads.length}`}
                                color={theme.colors.white}
                            />
                        )}
                        <div className={`logoutBox ${showMenu ? 'is-open' : ''}`}>
                            <Link
                                href={
                                    isUserLoggedIn
                                        ? buildLanguagePathname(routes.MY_ACCOUNT)
                                        : buildLanguagePathname(routes.LOGIN)
                                }
                            >
                                <a
                                    id="TESTING_MY_ACC"
                                    data-testid="TESTING_MY_ACC_STATEMENT"
                                    className="accountStatement"
                                    onClick={onMyAccountClick}
                                >
                                    {isBrandAP && <Icon height={24} name={Icons.Account} className="account" />}
                                    <p>{t('header.nav.accountStatement')}</p>
                                </a>
                            </Link>
                            <a
                                href="#"
                                onClick={logoutUser}
                                className="logoutButton"
                                data-testid="TESTING_LOGOUT_BUTTON"
                            >
                                <p>
                                    {isBrandAP && (
                                        <span className="logoutButtonIcon">
                                            <Icon height={24} name={Icons.Logout} className="account" />
                                        </span>
                                    )}
                                    <span className="logoutButtonText">{t('header.nav.logout')}</span>
                                </p>
                            </a>
                        </div>
                    </>
                );
            }

            return (
                <a href="#" onClick={logoutUser}>
                    <Icon height={24} name={Icons.Account} className="account" />
                    <p>{t('header.nav.logout')}</p>
                </a>
            );
        };

        const renderLoginBox = () => {
            return (
                !isBrandRecommendsJourneyAllowed && (
                    <a
                        href={
                            isUserLoggedIn && userAuth?.loginType !== EUserLoginType.GUEST
                                ? BRANDID_MY_ACCOUNT_URL
                                : buildLanguagePathname(routes.LOGIN)
                        }
                        id="TESTING_MY_ACC"
                        onClick={onMyAccountClick}
                    >
                        <Icon height={24} name={Icons.Account} className="account isLoggedOut" />
                        <p className="header__nav-description">{t('header.nav.myAccount')}</p>
                    </a>
                )
            );
        };

        const renderNavigation = () =>
            isUserLoggedIn && userAuth?.loginType !== EUserLoginType.GUEST ? renderLogoutBox(true) : renderLoginBox();

        const isEnvSwitcherEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_ENV_SWITCHER_ENABLED);

        const isContactDetailEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_CONTACT_DETAILS_ENABLED);

        const currentPath = router.asPath;
        const isTrimPage = currentPath.includes(routes.SELECTOR);
        const isBasketPage = currentPath.includes(routes.BASKET);
        const isConfigPage = currentPath.includes(routes.CAR);
        const renderB2BSwitch = () => {
            if (!(isTrimPage || isBasketPage || isConfigPage || isHomepage)) {
                return;
            }

            if (isEnvSwitcherEnabled && IS_B2B && B2C_URL) {
                return (
                    <RelativeLink href={B2C_URL}>
                        <a id="TESTING_B2C" className="link-wrapper link-wrapper-b2c">
                            <Icon
                                height={19}
                                name={isBrandDS ? Icons.StoreCarSolid : Icons.StoreCar}
                                className="storeCar"
                            />
                            <p className="header__nav-description">{t('header.nav.b2c')}</p>
                        </a>
                    </RelativeLink>
                );
            }

            if (isEnvSwitcherEnabled && IS_B2C && B2B_URL) {
                return (
                    <RelativeLink href={B2B_URL}>
                        <a className="link-wrapper" id="TESTING_B2B">
                            <Icon
                                height={19}
                                name={isBrandDS ? Icons.StoreCarSolid : Icons.StoreCar}
                                className="storeCar"
                            />
                            <p className="header__nav-description header__nav-description-b2b">{t('header.nav.b2b')}</p>
                        </a>
                    </RelativeLink>
                );
            }
        };

        const renderContactDetails = () => {
            return (
                <a
                    onClick={() => {
                        handleNeedHelpClick();
                        pushToDataLayer({
                            event: 'uaevent',
                            eventCategory: 'Header',
                            eventAction: 'Display::ContactUs',
                            eventLabel: t('header.nav.needHelp'),
                        } as GtmData);
                    }}
                    href="#"
                    ref={needHelpButtonRef}
                    className={`need-help-link ${isHelpSectionOpen ? 'need-help-open' : ''}`}
                >
                    <Icon name={Icons.Phone} className="need-help-icon" height={19} width={19} />

                    <p className="header__nav-description needHelp">
                        {t('header.nav.needHelp')}
                        <br />
                        <span className="header__nav-description_secondary">{t('support.telephone')}</span>
                    </p>

                    {isBrandAP && (
                        <Icon
                            height={10}
                            width={10}
                            name={isHelpSectionOpen ? Icons.ChevronUp : Icons.ChevronDown}
                            className={`need-help-link__chevron${isHelpSectionOpen ? ' isOpen' : ''}`}
                        />
                    )}
                    {isHelpSectionOpen && (
                        <>
                            <div
                                ref={needHelpRef}
                                className={`need-help-section ${
                                    [MARKETS.ES, MARKETS.DE, MARKETS.IT].includes(MARKET)
                                        ? 'need-help-section-right'
                                        : 'need-help-section-left'
                                }`}
                            >
                                <NeedHelpSection phoneNumber={t('support.telephone')} />
                            </div>
                            <div className="need-help-overlay" />
                        </>
                    )}
                </a>
            );
        };

        const createDeal = useCreateDeal();
        const onBasketRedirect = async () => {
            pushToDataLayer({
                eventCategory: 'Navigation',
                eventAction: 'ViewBasket',
                eventLabel: isStockJourney ? t('header.nav.stock.myBasket') : t('header.nav.myBasket'),
            } as GtmData);
            // when customer clicks icon on config page it should create deal as it is same as continue button
            if (isConfigPage) {
                try {
                    createDeal();
                } catch (e) {
                    /* creating deal can fail without any trouble here - empty basket */
                }
            }
            router.push(routes.BASKET);
        };
        const onHomePageRedirect = async () => {
            router.push(routes.ROOT);
        };

        return !isBrandRecommendsJourneyAllowed ? (
            <div className="navigation">
                {renderB2BSwitch()}
                {isContactDetailEnabled && renderContactDetails()}

                {renderNavigation()}

                <a href="#" role="button" id="TESTING_BASKET" onClick={onBasketRedirect}>
                    <Icon height={24} name={Icons.Basket} className="basket" />
                    <p className="header__nav-description">
                        {isStockJourney ? t('header.nav.stock.myBasket') : t('header.nav.myBasket')}
                    </p>
                </a>
                {isBrandAC && (
                    <div className="logo">
                        <a
                            data-testid={isBrandAC ? 'TESTING_LOGO_BTN' : ''}
                            className="logo"
                            onClick={onHomePageRedirect}
                        >
                            <Icon width={79} color={'red'} height={80} name={Icons.LogoMobile} />
                        </a>
                    </div>
                )}
            </div>
        ) : (
            <div className="navigation">{isContactDetailEnabled && renderContactDetails()}</div>
        );
    }
);
HeaderNavigation.displayName = 'HeaderNavigation';

export default HeaderNavigation;
